import { useEffect, useState } from 'react';
import { doc, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import type { Meeting } from '@penticton-dev-events/shared';

import { useFirebaseServices } from '../app/providers/FirebaseProvider';

export type UseMeetingState = {
  readonly meeting: Meeting | null;
  readonly loading: boolean;
  readonly error: Error | null;
};

export const useMeeting = (meetingId: string | null): UseMeetingState => {
  const { firestore } = useFirebaseServices();
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(meetingId));
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!meetingId) {
      console.log('useMeeting: No meetingId provided');
      setMeeting(null);
      setLoading(false);
      return () => undefined;
    }

    console.log('useMeeting: Subscribing to meeting:', meetingId);
    setLoading(true);
    setError(null);

    const meetingRef = doc(firestore, 'meetings', meetingId);

    const unsubscribe: Unsubscribe = onSnapshot(
      meetingRef,
      snapshot => {
        if (snapshot.exists()) {
          const meetingData = {
            id: snapshot.id,
            ...snapshot.data(),
          } as Meeting;
          console.log('useMeeting: Received meeting data:', meetingData);
          setMeeting(meetingData);
        } else {
          console.log('useMeeting: Meeting not found:', meetingId);
          setError(new Error('Meeting not found'));
          setMeeting(null);
        }
        setLoading(false);
      },
      err => {
        console.error('useMeeting: Error fetching meeting:', err);
        setError(err);
        setLoading(false);
      },
    );

    return () => {
      console.log('useMeeting: Unsubscribing from meeting:', meetingId);
      unsubscribe();
    };
  }, [firestore, meetingId]);

  return { meeting, loading, error };
};
